/**
 * Confirm Danger Modal
 * Requires typing confirmation text and has countdown before allowing action
 */

import React, { useState, useEffect } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import useEscClose from '../../hooks/useEscClose';

export default function ConfirmDangerModal({
  title,
  message,
  confirmText,
  confirmLabel = 'Escriba para confirmar:',
  onConfirm,
  onCancel,
  countdownSeconds = 5
}) {
  useEscClose(onCancel);

  const [inputValue, setInputValue] = useState('');
  const [countdown, setCountdown] = useState(countdownSeconds);
  const [loading, setLoading] = useState(false);

  const isMatch = inputValue === confirmText;
  const canConfirm = isMatch && countdown === 0;

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleConfirm = async () => {
    if (!canConfirm) return;

    setLoading(true);
    try {
      await onConfirm();
    } catch (error) {
      // Error handled by parent
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />

      <div className="relative w-full max-w-md bg-white border border-gray-200 rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200">
          <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onCancel} className="ml-auto text-gray-500 hover:text-gray-900">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-gray-700">{message}</p>

          <div>
            <label className="block text-sm text-gray-500 mb-2">
              {confirmLabel}
            </label>
            <div className="bg-gray-50 px-3 py-2 rounded-lg mb-2">
              <code className="text-red-700">{confirmText}</code>
            </div>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Escriba el texto exacto..."
              autoFocus
            />
          </div>

          {countdown > 0 && (
            <div className="bg-yellow-50 border border-yellow-500/20 rounded-lg px-4 py-3 text-center">
              <p className="text-yellow-600">
                Espere {countdown} segundo{countdown !== 1 ? 's' : ''} antes de confirmar
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm || loading}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
          >
            {loading && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {loading ? 'Procesando...' : countdown > 0 ? `Espere (${countdown})` : 'Confirmar Eliminación'}
          </button>
        </div>
      </div>
    </div>
  );
}
